import {EVENT_NAMES, USER_TYPES} from '/enum/tealium.enum';
import {getDefaultNewCoachesValue} from '/util/filter.util';
import {getTealiumUtilDefaultInstance} from '/util/tealium';

/**
 * Generates callback to show popover
 *
 * @param {object} params - Parameters
 * @param {function} params.showRecordPopover - A callback to show popover
 * @param {function} params.recordsShowPopover - A callback to show popover
 * @param {function} params.recordsShowRecord - A callback to show record
 * @param {function} params.onOrderPress - A callback to show order history
 *
 * @returns {function} - Callback - A function to be called when item is pressed
 */
export const onRefresh = (refreshing, resetMyTeamsData) => {
  refreshing = true;
  resetMyTeamsData();
  setTimeout(() => {
    refreshing = false;
  }, 200);
};

export function generateOnRecordPress({
  showRecordPopover,
  recordsShowPopover,
  recordsShowRecord,
  onOrderPress,
}: any) {
  return ({id}: any) => {
    if (typeof showRecordPopover === 'function') {
      showRecordPopover([id], {
        recordsShowPopover,
        recordsShowRecord,
        onOrderPress,
      });
    }
  };
}

export function generateOnPressTemOrg({
  sendEvent,
  onRecordPress,
  lastPaidRank,
  coachHighestRank,
  coachCurrentRank,
  previousScreenName,
  currentScreenName,
}: {
  sendEvent?: (params: any) => void;
  onRecordPress?: (item: any) => void;
  lastPaidRank?: string;
  coachHighestRank?: string;
  coachCurrentRank?: string;
  previousScreenName?: string;
  currentScreenName?: string;
}) {
  return (pressedItem: any) => {
    // Fire Tealium event if provided
    if (typeof sendEvent === 'function') {
      sendEvent({
        userType: USER_TYPES.coach,
        userFullName: pressedItem.firstName + ' ' + pressedItem.lastName,
        lastPaidRank,
        userId: String(pressedItem.id),
        currentScreenName,
        screenName: currentScreenName,
        previousScreenName,
        coachHighestRank,
        coachCurrentRank,
      });
    }

    if (typeof onRecordPress === 'function') {
      onRecordPress(pressedItem);
    }
  };
}

/**
 * Generates a callback to be called when filters are applied to My Teams Report
 *
 * @param {object} params - Parameters
 * @param {string} params.filterType - current filter type value
 * @param {string} params.filterSelected - current filter selected value
 * @param {string} params.lastPaidRank - coach's last paid rank
 * @param {string} params.highestAchievedRank - coach's highest achieved rank
 * @param {string} params.coachCurrentRank - coach's current rank
 * @param {string} params.coachId - coach's ID
 *
 * @returns {function} Callback - A function to be called when filters are applied
 */

export function generateOnApplyMyTeamsFilters({
  filter_type,
  filter_selected,
  filter_type2,
  filter_selected2,
  lastPaidRank,
  coachHighestRank,
  coachCurrentRank,
  coachId,
  reportName,
}: any) {
  const sendEvent = getTealiumUtilDefaultInstance().generateTrackEvent({
    eventName: EVENT_NAMES.onFilterPress,
  });
  sendEvent({
    filterType: filter_type,
    filterSelected: filter_selected,
    filterType2: filter_type2,
    filterSelected2: filter_selected2,
    reportName,
    lastPaidRank: lastPaidRank,
    highestAchievedRank: coachHighestRank,
    coachCurrentRank: coachCurrentRank,
    coachId: coachId,
  });
}

/**
 * Helper function to map filter values to Tealium format
 */
export const mapFilterTypeToTealium = (
  filterType: string,
  selectedValue: any,
  ffTeamsOrgSr: boolean,
) => {
  switch (filterType) {
    case 'rank':
      const rankMapping: {[key: string]: string} = {
        '1': 'coach',
        '10': 'SC',
        '20': 'manager',
        '40': 'director',
        '50': 'ED',
        '60': 'IED',
        '70': 'RD',
        '80': 'IRD',
        '90': 'ND',
        '100': 'IND',
        '110': 'GD',
        '120': 'IGD',
        '130': 'PD',
        '140': 'IPD',
      };
      return Array.isArray(selectedValue)
        ? selectedValue.map((id: string) => rankMapping[id] || 'na').join(',')
        : rankMapping[selectedValue] || 'na';

    case 'new_coach':
      const newCoachMappingSR: {[key: string]: string} = {
        none: 'Any time',
        current: 'Current month',
        previous: 'Last month',
        previous2: '2 months ago',
        combined: 'Last 3 months',
      };

      const newCoachMapping: {[key: string]: string} = {
        all: 'All Coaches',
        '30': 'Last 30 days',
        '60': 'Last 60 days',
        '90': 'Last 90 days',
      };

      const mapping = ffTeamsOrgSr ? newCoachMappingSR : newCoachMapping;

      const defaultValue = ffTeamsOrgSr ? 'Any time' : 'All Coaches';

      return mapping[selectedValue] || defaultValue;

    case 'fibc':
      const fibcMapping: {[key: string]: string} = {
        any: 'Any',
        yes: 'Yes',
        no: 'No',
      };
      return fibcMapping[selectedValue] || 'Any';

    default:
      return 'na';
  }
};

/**
 * Helper function to determine filter_type based on selected filters
 */
export const getFilterType = (
  rankFilter: any[],
  periodFilter: any,
  fibcFilter: any,
  frontline: boolean,
  ffTeamsOrgSR: boolean,
  closeToNextRank: boolean,
) => {
  const defaultNewCoachesValue = getDefaultNewCoachesValue(ffTeamsOrgSR);
  if (frontline) return 'frontline';
  if (rankFilter && rankFilter.length > 0) return 'ranks';
  if (periodFilter && periodFilter.id !== defaultNewCoachesValue)
    return 'new_coach';
  if (fibcFilter && fibcFilter.id !== 'any') return 'fibc';
  if (closeToNextRank) return 'closeToNextRank';
  return 'na';
};

/**
 * Helper function to map rank IDs to Tealium rank names for profile_update event
 */
export const mapRankIdToTealiumRankName = (rankId: string | number): string => {
  const rankMapping: {[key: string]: string} = {
    '1': 'OPTAVIA Coach',
    '10': 'Senior Coach',
    '20': 'Manager',
    '30': 'Associate Director',
    '40': 'Director',
    '50': 'Executive Director',
    '60': 'Integrated Executive Director',
    '70': 'Regional Director',
    '80': 'Integrated Regional Director',
    '90': 'National Director',
    '100': 'Integrated National Director',
    '110': 'Global Director',
    '120': 'Integrated Global Director',
    '130': 'Presidential Director',
    '140': 'Integrated Presidential Director',
  };
  return rankMapping[rankId?.toString()] || 'na';
};
